// Pure helpers for the policy rule editor.
//
// Kept out of the component because the interesting part is value coercion,
// and coercion is worth testing directly: the backend rejects `gt` against a
// string, so an editor that posts "500" instead of 500 fails on every numeric
// rule and gives the author no clue why.

export const PAYLOAD_PREFIX = "payload.";

export const blankCondition = (field = "action.type") => ({
  field,
  op: "eq",
  value: "",
  // Only consulted for payload fields, whose type the backend cannot know.
  valueType: "text",
});

export const blankRule = () => ({
  id: "",
  effect: "REQUIRE_APPROVAL",
  when: [blankCondition()],
  approval_roles: [],
  description: "",
});

export const isPayloadField = (field) =>
  typeof field === "string" && field.startsWith(PAYLOAD_PREFIX);

/** The catalogue entry for a field, or null for a payload one. */
export const descriptorFor = (catalogue, field) =>
  (catalogue?.fields || []).find((f) => f.field === field) || null;

/** Which operators this field may use. */
export function operatorsFor(catalogue, condition) {
  if (isPayloadField(condition.field)) return catalogue?.payload_operators || [];
  return descriptorFor(catalogue, condition.field)?.operators || [];
}

/** The value type to render an input for. */
export function valueTypeFor(catalogue, condition) {
  if (condition.op === "exists") return "bool";
  if (isPayloadField(condition.field)) return condition.valueType || "text";
  const descriptor = descriptorFor(catalogue, condition.field);
  return descriptor?.type || "string";
}

/**
 * Turn what the form holds into what the API expects.
 *
 * The form keeps everything as a string because that is what an <input> gives
 * back. The API validates types strictly, so the conversion has to happen
 * exactly once, here, rather than being half-done in three places.
 */
export function coerceValue(raw, type, op) {
  if (op === "exists") return raw === true || raw === "true";
  if (op === "in" || op === "not_in") {
    const parts = String(raw ?? "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    return type === "number" ? parts.map(Number).filter((n) => !Number.isNaN(n)) : parts;
  }
  if (type === "number") {
    const n = Number(raw);
    // NaN would serialise as null and read as "absent", which matches nothing.
    // Returning the raw string instead lets the server explain the mistake.
    return raw === "" || Number.isNaN(n) ? raw : n;
  }
  if (type === "bool") return raw === true || raw === "true";
  return raw;
}

/** The request body for a save. */
export function toPayload(rules, catalogue) {
  return rules.map((rule) => ({
    id: (rule.id || "").trim(),
    effect: rule.effect,
    description: (rule.description || "").trim(),
    approval_roles: rule.approval_roles || [],
    when: (rule.when || []).map((condition) => ({
      field: (condition.field || "").trim(),
      op: condition.op,
      value: coerceValue(
        condition.value,
        valueTypeFor(catalogue, condition),
        condition.op
      ),
    })),
  }));
}

/** Server shape -> form shape. */
export function fromRuleset(ruleset) {
  return (ruleset?.rules || []).map((rule) => ({
    id: rule.id || "",
    effect: rule.effect || "REQUIRE_APPROVAL",
    description: rule.description || "",
    approval_roles: rule.approval_roles || [],
    when: (rule.when || []).map((condition) => ({
      field: condition.field || "action.type",
      op: condition.op || "eq",
      value: Array.isArray(condition.value)
        ? condition.value.join(", ")
        : condition.value === null || condition.value === undefined
          ? ""
          : String(condition.value),
      valueType: typeof condition.value === "number" ? "number" : "text",
    })),
  }));
}

/** A one-line reading of a rule, for the collapsed card. */
export function summarise(rule, catalogue) {
  const labelFor = (field) =>
    descriptorFor(catalogue, field)?.label || field;
  const opLabel = (op) =>
    (catalogue?.operators || []).find((o) => o.op === op)?.label || op;

  const clauses = (rule.when || [])
    .map((c) => `${labelFor(c.field)} ${opLabel(c.op)} ${c.value === "" ? "…" : c.value}`)
    .join(" and ");

  const roles = (rule.approval_roles || []).length
    ? ` (${rule.approval_roles.join(", ")})`
    : "";
  return clauses ? `When ${clauses} → ${rule.effect}${roles}` : `→ ${rule.effect}${roles}`;
}

/**
 * Whether this rule would relax an action the engine protects anyway.
 *
 * The backend says the same thing, but only once a decision has been made.
 * Saying it while the rule is being written is the whole reason `floored`
 * is in the catalogue.
 */
export function relaxesAFlooredAction(rule, catalogue) {
  if (rule.effect !== "ALLOW") return false;
  const options = descriptorFor(catalogue, "action.type")?.options || [];
  return (rule.when || []).some(
    (c) =>
      c.field === "action.type" &&
      options.some((o) => o.value === c.value && o.floored)
  );
}

/** Group server validation errors by the rule they belong to. */
export function byRuleIndex(errors) {
  const grouped = {};
  (errors || []).forEach((error) => {
    const key = error.index ?? 0;
    grouped[key] = grouped[key] || [];
    grouped[key].push(error);
  });
  return grouped;
}
